import { IFormField } from "@/api/businessLinesApi";

export const renderField = ({
  field,
  editedField,
  setEditedField,
  handleFieldTypeChange,
  onFieldUpdate,
  isReadOnly = false,
}: {
  field: IFormField;
  editedField: IFormField;
  setEditedField: (field: IFormField) => void;
  handleFieldTypeChange: (type: IFormField["field_type"]) => void;
  onFieldUpdate: (field: IFormField) => void;
  isReadOnly?: boolean;
}) => {
  const fieldTypes = [
    { value: "text", label: "Text" },
    { value: "number", label: "Number" },
    { value: "email", label: "Email" },
    { value: "tel", label: "Phone" },
    { value: "radio", label: "Radio (Single Choice)" },
    { value: "checkbox", label: "Checkbox (Multiple Choices)" },
    { value: "select", label: "Select (Dropdown)" },
  ];

  const handleTitleChange = (newTitle: string) => {
    console.log("renderField: handleTitleChange called", {
      fieldId: field.id,
      newTitle,
      editedFieldTitle: editedField.title,
    });
    setEditedField({ ...editedField, title: newTitle });
    onFieldUpdate({ ...editedField, title: newTitle });
  };

  const baseInputClasses =
    "w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#78C7D1] select-text";

  // Prevent drag events from interfering with text selection
  const handleInputMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleInputMouseMove = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (field.is_default === false) {
    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={editedField.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            onMouseDown={handleInputMouseDown}
            onMouseMove={handleInputMouseMove}
            placeholder="Enter title"
            className={baseInputClasses}
            disabled={isReadOnly}
          />
          <select
            value={editedField.field_type}
            onChange={(e) =>
              handleFieldTypeChange(e.target.value as IFormField["field_type"])
            }
            onMouseDown={handleInputMouseDown}
            onMouseMove={handleInputMouseMove}
            className={`${baseInputClasses} w-48`}
            title="Select field type"
            aria-label="Select field type"
            disabled={isReadOnly}
          >
            {fieldTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        {(editedField.field_type === "text" ||
          editedField.field_type === "number" ||
          editedField.field_type === "tel" ||
          editedField.field_type === "email") && (
          <>
            <input
              type={editedField.field_type}
              value="filled by the operator"
              placeholder="Filled by the operator"
              className={`${baseInputClasses} disabled:bg-gray-100`}
              disabled
            />
            {/* Required & Verification Toggles for non-default fields */}
            <div className="flex items-center gap-6 justify-end mt-2">
              <div className="flex items-center gap-2">
                <span>Required</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editedField.required}
                    onChange={(e) => {
                      console.log("renderField: required checkbox changed", {
                        fieldId: field.id,
                        checked: e.target.checked,
                      });
                      const updatedField = {
                        ...editedField,
                        required: e.target.checked,
                      };
                      setEditedField(updatedField);
                      onFieldUpdate(updatedField);
                    }}
                    className="sr-only peer"
                    disabled={isReadOnly}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
                  <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
                </label>
              </div>
              <div className="flex items-center gap-2">
                <span>Verification Required</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editedField.verification_required}
                    onChange={(e) => {
                      const updatedField = {
                        ...editedField,
                        verification_required: e.target.checked,
                      };
                      setEditedField(updatedField);
                      onFieldUpdate(updatedField);
                    }}
                    className="sr-only peer"
                    disabled={isReadOnly}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
                  <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
                </label>
              </div>
            </div>
          </>
        )}
        {(editedField.field_type === "radio" ||
          editedField.field_type === "checkbox" ||
          editedField.field_type === "select") && (
          <div className="space-y-2">
            <div className="text-sm text-gray-600 mb-2">
              {editedField.field_type === "radio" && "Single choice options:"}
              {editedField.field_type === "checkbox" &&
                "Multiple choice options:"}
              {editedField.field_type === "select" && "Dropdown options:"}
            </div>
            {editedField.choices?.map((choice: string, index: number) => (
              <div key={index} className="flex items-center gap-2">
                <span className="text-gray-500">{index + 1}.</span>
                <input
                  type="text"
                  value={choice}
                  onChange={(e) => {
                    const newChoices = [...(editedField.choices || [])];
                    newChoices[index] = e.target.value;
                    const updatedField = {
                      ...editedField,
                      choices: newChoices,
                    };
                    setEditedField(updatedField);
                    onFieldUpdate(updatedField);
                  }}
                  onMouseDown={handleInputMouseDown}
                  onMouseMove={handleInputMouseMove}
                  className={baseInputClasses}
                  disabled={isReadOnly}
                  placeholder="Enter option"
                />
                {!isReadOnly && (
                  <button
                    onClick={() => {
                      const newChoices =
                        editedField.choices?.filter((_, i) => i !== index) ||
                        [];
                      const updatedField = {
                        ...editedField,
                        choices: newChoices,
                      };
                      setEditedField(updatedField);
                      onFieldUpdate(updatedField);
                    }}
                    className="px-2 py-1 text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <div className="flex items-center gap-2">
              <span className="text-gray-500">
                {(editedField.choices?.length || 0) + 1}.
              </span>
              {!isReadOnly && (
                <button
                  onClick={() => {
                    const updatedField = {
                      ...editedField,
                      choices: [...(editedField.choices || []), ""],
                    };
                    setEditedField(updatedField);
                    onFieldUpdate(updatedField);
                  }}
                  className="text-[#78C7D1] hover:text-[#6ab4be]"
                >
                  Add option
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  switch (field.field_type) {
    case "text":
      return (
        <div>
          <input
            type="text"
            value="Filled by the operator"
            placeholder="Filled by the operator"
            className={`${baseInputClasses} disabled:bg-gray-100`}
            disabled
          />
          {/* Required & Verification Toggles for default fields */}
          <div className="flex items-center gap-6 justify-end mt-2">
            <div className="flex items-center gap-2">
              <span>Required</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!field.required}
                  onChange={(e) => {
                    const updatedField = {
                      ...field,
                      required: e.target.checked,
                    };
                    setEditedField(updatedField);
                    onFieldUpdate(updatedField);
                  }}
                  className="sr-only peer"
                  disabled={isReadOnly}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
                <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <span>Verification Required</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!field.verification_required}
                  onChange={(e) => {
                    const updatedField = {
                      ...field,
                      verification_required: e.target.checked,
                    };
                    setEditedField(updatedField);
                    onFieldUpdate(updatedField);
                  }}
                  className="sr-only peer"
                  disabled={isReadOnly}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
                <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
              </label>
            </div>
          </div>
        </div>
      );
    case "number":
      return (
        <div>
          <input
            type="number"
            value="Filled by the operator"
            placeholder="Filled by the operator"
            className={`${baseInputClasses} disabled:bg-gray-100`}
            disabled
          />
          {/* Required & Verification Toggles for default fields */}
          <div className="flex items-center gap-6 justify-end mt-2">
            <div className="flex items-center gap-2">
              <span>Required</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!field.required}
                  onChange={(e) => {
                    const updatedField = {
                      ...field,
                      required: e.target.checked,
                    };
                    setEditedField(updatedField);
                    onFieldUpdate(updatedField);
                  }}
                  className="sr-only peer"
                  disabled={isReadOnly}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
                <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <span>Verification Required</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!field.verification_required}
                  onChange={(e) => {
                    const updatedField = {
                      ...field,
                      verification_required: e.target.checked,
                    };
                    setEditedField(updatedField);
                    onFieldUpdate(updatedField);
                  }}
                  className="sr-only peer"
                  disabled={isReadOnly}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
                <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
              </label>
            </div>
          </div>
        </div>
      );
    case "tel":
      return (
        <div>
          <input
            type="tel"
            value="Filled by the operator"
            placeholder="Filled by the operator"
            className={`${baseInputClasses} disabled:bg-gray-100`}
            disabled
          />
          {/* Required & Verification Toggles for default fields */}
          <div className="flex items-center gap-6 justify-end mt-2">
            <div className="flex items-center gap-2">
              <span>Required</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!field.required}
                  onChange={(e) => {
                    const updatedField = {
                      ...field,
                      required: e.target.checked,
                    };
                    setEditedField(updatedField);
                    onFieldUpdate(updatedField);
                  }}
                  className="sr-only peer"
                  disabled={isReadOnly}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
                <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <span>Verification Required</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!field.verification_required}
                  onChange={(e) => {
                    const updatedField = {
                      ...field,
                      verification_required: e.target.checked,
                    };
                    setEditedField(updatedField);
                    onFieldUpdate(updatedField);
                  }}
                  className="sr-only peer"
                  disabled={isReadOnly}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
                <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
              </label>
            </div>
          </div>
        </div>
      );
    case "email":
      return (
        <div>
          <input
            type={field.field_type}
            value="Filled by the operator"
            placeholder="Filled by the operator"
            className={`${baseInputClasses} disabled:bg-gray-100`}
            disabled
          />
          {/* Required & Verification Toggles for default fields */}
          <div className="flex items-center gap-6 justify-end mt-2">
            <div className="flex items-center gap-2">
              <span>Required</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!field.required}
                  onChange={(e) => {
                    const updatedField = {
                      ...field,
                      required: e.target.checked,
                    };
                    setEditedField(updatedField);
                    onFieldUpdate(updatedField);
                  }}
                  className="sr-only peer"
                  disabled={isReadOnly}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
                <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <span>Verification Required</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!field.verification_required}
                  onChange={(e) => {
                    const updatedField = {
                      ...field,
                      verification_required: e.target.checked,
                    };
                    setEditedField(updatedField);
                    onFieldUpdate(updatedField);
                  }}
                  className="sr-only peer"
                  disabled={isReadOnly}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
                <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
              </label>
            </div>
          </div>
        </div>
      );
    case "select":
      return (
        <div>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">Dropdown selection:</div>
            <select
              className={`${baseInputClasses} disabled:bg-gray-100`}
              disabled
              value="Filled by the operator"
              title="Dropdown selection"
              aria-label="Dropdown selection"
            >
              <option value="Filled by the operator">
                Filled by the operator
              </option>
              {field.choices?.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </div>
          {/* Required Toggle for default fields */}
          <div className="flex items-center gap-2 mt-2">
            <span className="ml-auto">Required</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!field.required}
                onChange={(e) => {
                  const updatedField = {
                    ...field,
                    required: e.target.checked,
                  };
                  setEditedField(updatedField);
                  onFieldUpdate(updatedField);
                }}
                className="sr-only peer"
                disabled={isReadOnly}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
              <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
            </label>
          </div>
          {/* Verification Required Toggle for default fields */}
          <div className="flex items-center gap-2 mt-2">
            <span className="ml-auto">Verification Required</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!field.verification_required}
                onChange={(e) => {
                  const updatedField = {
                    ...field,
                    verification_required: e.target.checked,
                  };
                  setEditedField(updatedField);
                  onFieldUpdate(updatedField);
                }}
                className="sr-only peer"
                disabled={isReadOnly}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
              <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
            </label>
          </div>
        </div>
      );
    case "radio":
      return (
        <div>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">
              Single choice (select one):
            </div>
            <div className="space-y-2">
              {field.choices?.map((choice, index) => (
                <label
                  key={index}
                  className="flex items-center gap-2 cursor-not-allowed opacity-50"
                >
                  <input
                    type="radio"
                    name={`radio-${field.id || "default"}`}
                    value={choice}
                    className="text-[#78C7D1] focus:ring-[#78C7D1]"
                    disabled
                  />
                  <span className="text-gray-700">{choice}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Required Toggle for default fields */}
          <div className="flex items-center gap-2 mt-2">
            <span className="ml-auto">Required</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!field.required}
                onChange={(e) => {
                  const updatedField = {
                    ...field,
                    required: e.target.checked,
                  };
                  setEditedField(updatedField);
                  onFieldUpdate(updatedField);
                }}
                className="sr-only peer"
                disabled={isReadOnly}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
              <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
            </label>
          </div>
          {/* Verification Required Toggle for default fields */}
          <div className="flex items-center gap-2 mt-2">
            <span className="ml-auto">Verification Required</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!field.verification_required}
                onChange={(e) => {
                  const updatedField = {
                    ...field,
                    verification_required: e.target.checked,
                  };
                  setEditedField(updatedField);
                  onFieldUpdate(updatedField);
                }}
                className="sr-only peer"
                disabled={isReadOnly}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
              <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
            </label>
          </div>
        </div>
      );
    case "checkbox":
      return (
        <div>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">
              Multiple choices (select all that apply):
            </div>
            <div className="space-y-2">
              {field.choices?.map((choice, index) => (
                <label
                  key={index}
                  className="flex items-center gap-2 cursor-not-allowed opacity-50"
                >
                  <input
                    type="checkbox"
                    value={choice}
                    className="text-[#78C7D1] rounded focus:ring-[#78C7D1]"
                    disabled
                  />
                  <span className="text-gray-700">{choice}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Required Toggle for default fields */}
          <div className="flex items-center gap-2 mt-2">
            <span className="ml-auto">Required</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!field.required}
                onChange={(e) => {
                  const updatedField = {
                    ...field,
                    required: e.target.checked,
                  };
                  setEditedField(updatedField);
                  onFieldUpdate(updatedField);
                }}
                className="sr-only peer"
                disabled={isReadOnly}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
              <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
            </label>
          </div>
          {/* Verification Required Toggle for default fields */}
          <div className="flex items-center gap-2 mt-2">
            <span className="ml-auto">Verification Required</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!field.verification_required}
                onChange={(e) => {
                  const updatedField = {
                    ...field,
                    verification_required: e.target.checked,
                  };
                  setEditedField(updatedField);
                  onFieldUpdate(updatedField);
                }}
                className="sr-only peer"
                disabled={isReadOnly}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#78C7D1] rounded-full peer peer-checked:bg-[#78C7D1] transition-all"></div>
              <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:translate-x-full"></div>
            </label>
          </div>
        </div>
      );
    default:
      return null;
  }
};
